import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { isEmpty, filter } from "lodash";

import { useUserLocation } from "~/context/UserLocationContext";

import ImageLink from "~/components/links/ImageLink";
import { MobileMenuToggle, SearchButton } from "~/components/Header";
import MainMenu from "~/components/Header/MainNav";
import MobileSecondary from "~/components/Header/MobileSecondary";

import IcoIcon from "~/components/IcoIcon";

import transition from "~/utilities/transition";
import { screenReaderText } from "~/styles/screenReaderText";

import "flag-icons/css/flag-icons.min.css";

const MainNavLinksWrapper = styled.nav`
    justify-content: space-between;
    align-items: center;
    display: ${({ mobileMenuIsOpen }) => (mobileMenuIsOpen ? `flex` : `none`)};

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        display: flex;
        width: 100%;
    }
`;

const Logo = styled.div`
    width: 100%;
    max-width: 200px;
    display: none;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        display: block;
        max-width: 230px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        display: block;
        max-width: 315px;
    }
`;

const MainNav = styled.nav`
    ${transition({}, "opacity", "visibility")};
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 0;
    right: 0;
    opacity: ${({ mobileMenuIsOpen }) => (mobileMenuIsOpen ? `1` : `0`)};
    visibility: ${({ mobileMenuIsOpen }) =>
        mobileMenuIsOpen ? `visible` : `hidden`};
    background-color: ${({ theme }) => theme.palette.whiteOverlay};
    z-index: ${({ theme }) => theme.zIndex.mainNav};
    padding: 10px 40px 0px 40px;
    height: 100vh;
    width: 80%;
    max-width: 310px;
    overflow-y: ${({ mobileMenuIsOpen }) =>
        mobileMenuIsOpen ? `auto` : `hidden`};
    overflow-x: hidden;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        flex-direction: row;
        position: static;
        visibility: visible;
        opacity: 1;
        background-color: inherit;
        padding: inherit;
        min-height: 90px;
        height: auto;
        max-width: fit-content;
        overflow: visible;
    }
`;

const ScreenReaderText = styled.h2`
    ${screenReaderText};
`;

const MobileCloseWrapper = styled.div`
    padding: 10px 0 15px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        display: none;
    }
`;

const SubNavBackButton = styled.button`
    ${transition({}, "opacity", "visibility")};
    font-weight: 500;
    font-size: 1.8rem;
    letter-spacing: 0.056em;
    color: ${({ theme }) => theme.palette.darkBlue};
    opacity: ${({ subNavOpen }) => (subNavOpen ? "1" : "0")};
    visibility: ${({ subNavOpen }) => (subNavOpen ? "visible" : "hidden")};
`;

const MainNavMobileWrapper = styled.div`
    border-bottom: 1px solid ${({ theme }) => theme.palette.darkGray};
    display: flex;
    flex-direction: column;
    margin-bottom: ${({ theme }) => theme.layout.gutter};

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        border-bottom: none;
        margin-bottom: inherit;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        border-bottom: none;
        flex-direction: row;
        margin-bottom: inherit;
    }
`;

const MobileSecondaryNav = styled.div`
    display: flex;
    flex-direction: column;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        display: none;
    }
`;

const SearchWrapper = styled.div`
    display: none;
    margin-left: ${({ theme }) => theme.layout.gutter};

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        display: flex;
    }
`;

const ExtraLinks = styled.div`
    display: flex;
    flex-direction: column;
    width: max-content;
    gap: 1rem;
    padding: 1rem 0 1rem 1.5rem;
    a {
        padding-bottom: 0;
    }
`;

const MobileCountriesWrapper = styled.div`
    border-top: 1px solid ${({ theme }) => theme.palette.darkGray};
    padding-top: 24px;
    margin-top: 24px;
`;

const CountriesDropdown = styled.div`
    display: flex;
    position: relative;
`;

const CountriesDropdownList = styled.ul`
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    display: ${({ open }) => (open ? "block" : "none")};
    background: ${({ theme }) => theme.palette.white};
    padding: 24px 10px;
`;

const Flag = styled.span`
    margin-right: 5px;
`;

const CountriesDropdownButton = styled.button`
    display: flex;
    align-items: center;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        display: none;
    }

    svg {
        margin-left: 5px;
        ${transition({}, "transform")};
        ${({ open }) => (open ? "transform: rotate(-180deg);" : "")}
    }
`;

const CountriesButton = styled.button`
    appearance: none;
    background: none;
    border: none;
    display: flex;
    align-items: center;
`;

const hasSecondaryLinks = (topNav, locationSiteLinks, languages) => {
    return (
        topNav?.length ||
        locationSiteLinks?.additionalSiteLocations?.length ||
        languages?.length
    );
};

const HeaderNav = ({
    headerLogo = null,
    topNav = [],
    mainNav = [],
    mobileMenuIsOpen = false,
    mainNavRef = null,
    mobileSubNavIsOpen = "",
    handleDispatch,
    mobileNavCloseFocusManagement,
    openMainSubnav = "",
    languages,
    locationSiteLinks,
}) => {
    const { userLocation, setUserLocation, countries } = useUserLocation();
    const allOtherCountries = filter(
        countries,
        (country) => country.slug !== userLocation.slug,
    );

    const [countriesDropdownOpen, setCountriesDropdownOpen] = useState(false);

    return (
        <MainNavLinksWrapper
            {...{ mobileMenuIsOpen }}
            aria-modal="true"
            role="dialog"
            aria-labelledby="mainNavLabel"
        >
            <Logo>
                {headerLogo ? <ImageLink to="/" image={headerLogo} /> : null}
            </Logo>

            <MainNav
                {...{ mobileMenuIsOpen }}
                aria-label="Main"
                ref={mainNavRef}
            >
                <ScreenReaderText id="mainNavLabel">
                    Main Navigation
                </ScreenReaderText>
                {mobileMenuIsOpen ? (
                    <MobileCloseWrapper>
                        <SubNavBackButton
                            onClick={() =>
                                handleDispatch("HANDLE_MOBILE_SUB_NAV_CLOSE")
                            }
                            subNavOpen={mobileSubNavIsOpen}
                            data-label="mobileSubNavClose"
                            aria-label="Return to main navigation"
                        >
                            <IcoIcon icon="arrow-left" />
                        </SubNavBackButton>
                        <MobileMenuToggle
                            onClick={() => {
                                handleDispatch(
                                    "HANDLE_MOBILE_NAVIGATION_CLOSE",
                                );
                                mobileNavCloseFocusManagement();
                            }}
                            {...{ mobileMenuIsOpen }}
                            type="button"
                            data-label="mobileMenuClose"
                            aria-label="Close navigation"
                        >
                            <IcoIcon icon="close" />
                        </MobileMenuToggle>
                    </MobileCloseWrapper>
                ) : null}

                {mainNav?.length ? (
                    <MainNavMobileWrapper>
                        {mainNav.map(({ linkLabel, ...rest }, index) => (
                            <MainMenu
                                {...{
                                    ...rest,
                                    handleDispatch,
                                    linkLabel,
                                    index,
                                    mobileMenuIsOpen,
                                    mobileSubNavIsOpen,
                                    openMainSubnav,
                                }}
                                key={`${linkLabel}-${index}`}
                            />
                        ))}
                    </MainNavMobileWrapper>
                ) : null}

                {hasSecondaryLinks(topNav, locationSiteLinks, languages) ? (
                    <MobileSecondaryNav id="mobileSecondaryNav">
                        {topNav?.length ? (
                            <>
                                {topNav.map(({ linkLabel, linkUrl }, index) => (
                                    <MobileSecondary
                                        {...{ linkLabel, linkUrl, index }}
                                        navLength={topNav.length - 1}
                                        key={`${linkLabel}-${index}`}
                                    />
                                ))}
                            </>
                        ) : null}

                        {locationSiteLinks.additionalSiteLocations.length ? (
                            <>
                                <h2>Countries</h2>
                                <ExtraLinks>
                                    {locationSiteLinks.additionalSiteLocations.map(
                                        ({ linkUrl, linkLabel }, index) => (
                                            <MobileSecondary
                                                {...{
                                                    linkLabel,
                                                    linkUrl,
                                                    index,
                                                }}
                                                navLength={
                                                    locationSiteLinks
                                                        .additionalSiteLocations
                                                        .length - 1
                                                }
                                                key={`${index}-${linkLabel}`}
                                            >
                                                {linkLabel}
                                            </MobileSecondary>
                                        ),
                                    )}
                                </ExtraLinks>
                            </>
                        ) : null}

                        {languages.length ? (
                            <>
                                <h2>Languages</h2>
                                <ExtraLinks>
                                    {languages.map(
                                        (
                                            { native_name, code, link },
                                            index,
                                        ) => (
                                            <MobileSecondary
                                                {...{
                                                    linkLabel: native_name,
                                                    linkUrl: link,
                                                    index,
                                                }}
                                                navLength={languages.length - 1}
                                                key={`${index}-${code}`}
                                            >
                                                {native_name}
                                            </MobileSecondary>
                                        ),
                                    )}
                                </ExtraLinks>
                            </>
                        ) : null}
                    </MobileSecondaryNav>
                ) : null}

                {!isEmpty(countries) ? (
                    <MobileCountriesWrapper>
                        <CountriesDropdown>
                            <CountriesDropdownButton
                                onClick={() =>
                                    setCountriesDropdownOpen(
                                        !countriesDropdownOpen,
                                    )
                                }
                                open={countriesDropdownOpen}
                            >
                                <Flag
                                    className={`fi fi-${userLocation.slug}`}
                                />
                                {userLocation.name}
                                <IcoIcon icon="caret-down" size={14} />
                            </CountriesDropdownButton>
                            <CountriesDropdownList open={countriesDropdownOpen}>
                                {allOtherCountries.map((country) => (
                                    <li key={country.slug}>
                                        <CountriesButton
                                            onClick={() => {
                                                setUserLocation(country.slug);
                                                setCountriesDropdownOpen(false);
                                            }}
                                        >
                                            <Flag
                                                className={`fi fi-${country.slug}`}
                                            />
                                            {country.name}
                                        </CountriesButton>
                                    </li>
                                ))}
                            </CountriesDropdownList>
                        </CountriesDropdown>
                    </MobileCountriesWrapper>
                ) : null}

                <SearchWrapper
                    onClick={() =>
                        handleDispatch("HANDLE_SEARCH_TOGGLE", "search", true)
                    }
                >
                    <SearchButton
                        isOpen={openMainSubnav === "search"}
                        type="button"
                        aria-expanded={openMainSubnav === "search"}
                        aria-label="Search"
                    >
                        <IcoIcon icon="search" />
                    </SearchButton>
                </SearchWrapper>
            </MainNav>
        </MainNavLinksWrapper>
    );
};

HeaderNav.propTypes = {
    headerLogo: PropTypes.shape({
        sourceUrl: PropTypes.string,
        altText: PropTypes.string,
        title: PropTypes.string.isRequired,
    }),
    topNav: PropTypes.arrayOf(
        PropTypes.shape({
            linkLabel: PropTypes.string.isRequired,
            linkUrl: PropTypes.string.isRequired,
            subNavigationItems: PropTypes.arrayOf(
                PropTypes.shape({
                    linkUrl: PropTypes.string.isRequired,
                    linkLabel: PropTypes.string.isRequired,
                }),
            ),
        }),
    ),
    mainNav: PropTypes.arrayOf(
        PropTypes.shape({
            linkLabel: PropTypes.string.isRequired,
            linkUrl: PropTypes.string.isRequired,
            singleColumnSubNavigation: PropTypes.bool,
            type: PropTypes.oneOf([
                "links",
                "cards",
                "iconLinks",
                "noSubNavigation",
            ]).isRequired,
            links: PropTypes.arrayOf(
                PropTypes.shape({
                    linkLabel: PropTypes.string.isRequired,
                    linkUrl: PropTypes.string.isRequired,
                    subNavigation: PropTypes.arrayOf(
                        PropTypes.shape({
                            linkUrl: PropTypes.string.isRequired,
                            linkLabel: PropTypes.string.isRequired,
                        }),
                    ),
                }),
            ),
            cards: PropTypes.arrayOf(
                PropTypes.shape({
                    logo: PropTypes.shape({
                        sourceUrl: PropTypes.string,
                        altText: PropTypes.string,
                        title: PropTypes.string.isRequired,
                    }),
                    logoBackgroundColor: PropTypes.string,
                    title: PropTypes.string,
                    subTitle: PropTypes.string,
                    productsImage: PropTypes.shape({
                        sourceUrl: PropTypes.string,
                        altText: PropTypes.string,
                        title: PropTypes.string.isRequired,
                    }),
                    buttonLabel: PropTypes.string,
                    buttonUrl: PropTypes.string,
                    additionalLinks: PropTypes.arrayOf(
                        PropTypes.shape({
                            linkLabel: PropTypes.string.isRequired,
                            linkUrl: PropTypes.string.isRequired,
                        }),
                    ),
                }),
            ),
            iconLinks: PropTypes.arrayOf(
                PropTypes.shape({
                    iconName: PropTypes.string,
                    linkUrl: PropTypes.string.isRequired,
                    linkLabel: PropTypes.string.isRequired,
                }),
            ),
        }),
    ),
    mobileMenuIsOpen: PropTypes.bool,
    mainNavRef: PropTypes.object,
    mobileSubNavIsOpen: PropTypes.string,
    handleDispatch: PropTypes.func.isRequired,
    mobileNavCloseFocusManagement: PropTypes.func.isRequired,
    openMainSubnav: PropTypes.string,
};

export default HeaderNav;
